
/*
 * Copyright (c) 2026, Oracle and/or its affiliates.
 */

/**
 * @file ACS.PDPSelection.AutoSelect.js
 * @author Oracle Generative AI
 * @reviewer Prapat Maneerat
 * @version 1.0.0
 * @date 2026-05-06
 *
 * @remarks
 * 1.0.0 - Initial SuiteCommerce Standard PDP matrix singleton option auto-selection.
 *
 * @description
 * Auto-selects PDP matrix item options that expose exactly one selectable value.
 */
define('ACS.PDPSelection.AutoSelect', [], function () {
    'use strict';

    var MODULE_NAME = 'ACS.PDPSelection.AutoSelect';
    var MINIMUM_SINGLETON_VALUES = 1;
    var intAutoSelectTimer = null;
    var bIsAutoSelecting = false;

    /**
     * Returns an array from an array-like object or object map.
     *
     * @param {*} objCollection Collection returned by SuiteCommerce item info.
     * @returns {Array} Normalized array.
     */
    function toArray(objCollection) {
        if (Array.isArray(objCollection)) {
            return objCollection;
        }

        if (objCollection && typeof objCollection === 'object') {
            return Object.keys(objCollection).map(function (strKey) {
                return objCollection[strKey];
            });
        }

        return [];
    }

    /**
     * Tests NetSuite-style boolean values.
     *
     * @param {*} value Value to test.
     * @returns {boolean} True when the value is a true-like flag.
     */
    function isTrue(value) {
        return value === true || value === 'T' || value === 'true' || value === '1' || value === 1;
    }

    /**
     * Tests NetSuite-style false values.
     *
     * @param {*} value Value to test.
     * @returns {boolean} True when the value is a false-like flag.
     */
    function isFalse(value) {
        return value === false || value === 'F' || value === 'false' || value === '0' || value === 0;
    }

    /**
     * Writes browser-safe diagnostics without interrupting the PDP.
     *
     * @param {string} strLevel Console level.
     * @param {string} strMessage Message to write.
     * @param {*} objDetails Optional diagnostic payload.
     * @returns {void}
     */
    function writeLog(strLevel, strMessage, objDetails) {
        var objConsole = typeof window !== 'undefined' && window.console;

        if (objConsole && typeof objConsole[strLevel] === 'function') {
            objConsole[strLevel](MODULE_NAME + ': ' + strMessage, objDetails || '');
        }
    }

    /**
     * Gets item option detail fields from the item info payload.
     *
     * @param {Object} objItemInfo PDP item information.
     * @returns {Array} Item option detail fields.
     */
    function getItemOptionFields(objItemInfo) {
        var objItem = objItemInfo && objItemInfo.item;
        var objOptionDetails = objItem && objItem.itemoptions_detail;

        return toArray(objOptionDetails && objOptionDetails.fields);
    }

    /**
     * Determines whether an item option detail field is a matrix dimension.
     *
     * @param {Object} objField Item option detail field.
     * @returns {boolean} True when the field is marked as a matrix dimension.
     */
    function isMatrixField(objField) {
        return !!objField && (
            isTrue(objField.ismatrixdimension) ||
            isTrue(objField.isMatrixDimension) ||
            isTrue(objField.matrixdimension) ||
            isTrue(objField.matrixDimension)
        );
    }

    /**
     * Builds a lookup of option identifiers that are known matrix dimensions.
     *
     * @param {Object} objItemInfo PDP item information.
     * @returns {Object} Matrix option identifier lookup.
     */
    function getMatrixOptionIdLookup(objItemInfo) {
        var objMatrixOptionIds = {};

        getItemOptionFields(objItemInfo).forEach(function (objField) {
            if (isMatrixField(objField)) {
                [
                    objField.cartOptionId,
                    objField.cartoptionid,
                    objField.itemOptionId,
                    objField.itemoptionid,
                    objField.internalid,
                    objField.id
                ].forEach(function (strId) {
                    if (strId) {
                        objMatrixOptionIds[String(strId)] = true;
                    }
                });
            }
        });

        return objMatrixOptionIds;
    }

    /**
     * Determines whether an option is included in a matrix option lookup.
     *
     * @param {Object} objOption PDP option.
     * @param {Object} objMatrixOptionIds Matrix option identifier lookup.
     * @returns {boolean} True when the option is a matrix dimension.
     */
    function isMatrixOption(objOption, objMatrixOptionIds) {
        return !!(
            objOption &&
            (
                objMatrixOptionIds[String(objOption.cartOptionId)] ||
                objMatrixOptionIds[String(objOption.itemOptionId)] ||
                objMatrixOptionIds[String(objOption.internalid)] ||
                objMatrixOptionIds[String(objOption.id)]
            )
        );
    }

    /**
     * Gets the currently selected internal ID for an option.
     *
     * @param {Object} objOption PDP option.
     * @returns {string|null} Selected value internal ID.
     */
    function getSelectedValueId(objOption) {
        var objValue = objOption && objOption.value;

        if (objValue && typeof objValue === 'object') {
            return objValue.internalid ? String(objValue.internalid) : null;
        }

        return objValue ? String(objValue) : null;
    }

    /**
     * Determines whether an option value is available for selection.
     *
     * @param {Object} objValue PDP option value.
     * @returns {boolean} True when the value can be selected.
     */
    function isSelectableValue(objValue) {
        var strInternalId = objValue && objValue.internalid;

        if (!strInternalId || String(strInternalId) === '-1') {
            return false;
        }

        return !(
            isFalse(objValue.isAvailable) ||
            isFalse(objValue.isavailable) ||
            isFalse(objValue.isEnabled) ||
            isFalse(objValue.isenabled) ||
            isTrue(objValue.isDisabled) ||
            isTrue(objValue.isdisabled)
        );
    }

    /**
     * Gets selectable values for a PDP option.
     *
     * @param {Object} objOption PDP option.
     * @returns {Array} Selectable option values.
     */
    function getSelectableValues(objOption) {
        return toArray(objOption && objOption.values).filter(isSelectableValue);
    }

    /**
     * Determines whether the current PDP item has matrix children.
     *
     * @param {Object} objPdp PDP component.
     * @param {Object} objItemInfo PDP item information.
     * @returns {boolean} True when the item is a matrix parent.
     */
    function isMatrixItem(objPdp, objItemInfo) {
        var arrMatrixChildren = [];
        var objItem = objItemInfo && objItemInfo.item;
        var arrEmbeddedChildren = toArray(objItem && objItem.matrixchilditems_detail);

        try {
            arrMatrixChildren = toArray(objPdp && objPdp.getAllMatrixChilds && objPdp.getAllMatrixChilds());
        } catch (objError) {
            writeLog('debug', 'Unable to read matrix children from PDP component.', objError);
        }

        return arrMatrixChildren.length > 0 ||
            arrEmbeddedChildren.length > 0 ||
            getItemOptionFields(objItemInfo).some(isMatrixField);
    }

    /**
     * Returns the matrix options to evaluate for singleton selection.
     *
     * @param {Object} objItemInfo PDP item information.
     * @returns {Array} Matrix PDP options.
     */
    function getMatrixOptions(objItemInfo) {
        var arrOptions = toArray(objItemInfo && objItemInfo.options).filter(function (objOption) {
            return objOption && objOption.cartOptionId && getSelectableValues(objOption).length > 0;
        });
        var objMatrixOptionIds = getMatrixOptionIdLookup(objItemInfo);
        var bHasMatrixLookup = Object.keys(objMatrixOptionIds).length > 0;
        var arrMatrixOptions = arrOptions.filter(function (objOption) {
            return isMatrixOption(objOption, objMatrixOptionIds);
        });

        return bHasMatrixLookup && arrMatrixOptions.length ? arrMatrixOptions : arrOptions;
    }

    /**
     * Builds a list of missing singleton option values that can be safely selected.
     *
     * @param {Array} arrMatrixOptions PDP matrix options.
     * @returns {Array} Option selections to set.
     */
    function getSingletonSelections(arrMatrixOptions) {
        var arrSelections = [];
        arrMatrixOptions.forEach(function (objOption) {
            var arrSelectableValues = getSelectableValues(objOption);
            var objSingletonValue = arrSelectableValues[0];
            var strCurrentValueId = getSelectedValueId(objOption);
            var strSingletonValueId = objSingletonValue && String(objSingletonValue.internalid);

            if (arrSelectableValues.length !== MINIMUM_SINGLETON_VALUES) {
                return;
            }

            if (strSingletonValueId && strCurrentValueId !== strSingletonValueId) {
                arrSelections.push({
                    cartOptionId: objOption.cartOptionId,
                    valueId: strSingletonValueId
                });
            }
        });

        return arrSelections;
    }

    /**
     * Auto-selects singleton matrix options on the current PDP.
     *
     * @param {Object} objPdp PDP component.
     * @returns {void}
     */
    function autoSelectSingletonMatrixOptions(objPdp) {
        var objItemInfo;
        var arrMatrixOptions;
        var arrSelections;

        if (bIsAutoSelecting || !objPdp) {
            return;
        }

        try {
            objItemInfo = objPdp.getItemInfo && objPdp.getItemInfo();

            if (!objItemInfo || !isMatrixItem(objPdp, objItemInfo)) {
                return;
            }

            arrMatrixOptions = getMatrixOptions(objItemInfo);
            arrSelections = getSingletonSelections(arrMatrixOptions);

            if (!arrSelections.length) {
                return;
            }

            bIsAutoSelecting = true;

            arrSelections.forEach(function (objSelection) {
                objPdp.setOption(objSelection.cartOptionId, objSelection.valueId);
            });

            writeLog('debug', 'Auto-selected singleton matrix options.', arrSelections);
        } catch (objError) {
            writeLog('error', 'Failed to auto-select PDP matrix options.', objError);
        } finally {
            bIsAutoSelecting = false;
        }
    }

    /**
     * Queues auto-selection after SuiteCommerce finishes rendering PDP content.
     *
     * @param {Object} objPdp PDP component.
     * @returns {void}
     */
    function queueAutoSelect(objPdp) {
        if (intAutoSelectTimer) {
            clearTimeout(intAutoSelectTimer);
        }

        intAutoSelectTimer = setTimeout(function () {
            intAutoSelectTimer = null;
            autoSelectSingletonMatrixOptions(objPdp);
        }, 0);
    }

    return {
        /**
         * Wires PDP singleton matrix option auto-selection into SuiteCommerce.
         *
         * @param {Object} container SuiteCommerce extension container.
         * @returns {void}
         */
        mountToApp: function mountToApp(container) {
            var objPdp = container && container.getComponent && container.getComponent('PDP');
            var objLayout = container && container.getComponent && container.getComponent('Layout');

            if (!objPdp || !objLayout) {
                writeLog('debug', 'PDP or Layout component is unavailable.');
                return;
            }

            objLayout.on('afterShowContent', function () {
                queueAutoSelect(objPdp);
            });
        }
    };
});
